import React, { useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';

// Components
import { TextComponent } from 'baseComponents/TextComponent';
import { DatePickerComponent } from 'baseComponents/DatePickerComponent';
import { TimePickerComponent } from 'baseComponents/TimePickerComponent';
import { TabContentWrapper } from 'screens/CreateCampaignScreen/components/TabContentWrapper';

// Helpers
import { disabledDate, disabledTime } from 'helpers/scheduling-options';

// Styles
import './schedulingOptionsTab.scss';

// store
import {
  useCreateCampaignStore,
  useCreateCampaignStoreActions,
} from 'store/CreateCampaignStore';

// constants
import { CREATE_CAMPAIGN_SCREEN } from 'constants/enums/create-campaign-screen';
import { InputComponent } from 'baseComponents/InputComponent';

export const SchedulingOptionsTab: React.FC = () => {
  // zustand store
  const sendDate = useCreateCampaignStore((state) => state.sendDate);
  const sendTime = useCreateCampaignStore((state) => state.sendTime);
  const name = useCreateCampaignStore((state) => state.name);

  const { setSendDate, setSendTime, setName } = useCreateCampaignStoreActions();

  const handleDateChange = useCallback((chosenDate: Dayjs | null) => {
    if (chosenDate) {
      setSendDate(chosenDate);
    }
    // eslint-disable-next-line
  }, []);

  const handleTimeChange = useCallback((chosenTime: Dayjs | null) => {
    if (chosenTime) {
      setSendTime(chosenTime);
    }
    // eslint-disable-next-line
  }, []);

  const defaultTime = dayjs().set('hour', 9).set('minute', 0);
  // timepicker value - default 9am
  const setTime = () => {
    if (typeof sendTime === 'string') {
      return dayjs(sendTime);
    }
    if (sendTime) {
      return sendTime;
    }
    setSendTime(defaultTime);
    return defaultTime;
  };

  const getDateString = (date: Dayjs | string) => {
    if (dayjs.isDayjs(date)) {
      return date.format('D MMMM YYYY');
    }
    if (typeof date === 'string') {
      return dayjs(date).format('D MMMM YYYY');
    }
    return '';
  };

  const getTimeString = (time: Dayjs | string) => {
    if (dayjs.isDayjs(time)) {
      return time.format('HH:mm');
    }
    if (typeof time === 'string') {
      return dayjs(time).format('HH:mm');
    }
    return '';
  };

  return (
    <TabContentWrapper tabTitle={CREATE_CAMPAIGN_SCREEN.CAMPAIGN_SETUP}>
      <div className='scheduling-options-form-wrapper'>
        <div className='picker-wrapper'>
          <InputComponent
            value={name}
            fullWidth
            onChange={(e) => setName(e.target.value)}
            labelValue={CREATE_CAMPAIGN_SCREEN.CAMPAIGN_NAME}
          />
        </div>
        <div className='picker-wrapper'>
          <DatePickerComponent
            disabledDate={disabledDate}
            value={typeof sendDate === 'string' ? dayjs(sendDate) : sendDate}
            onChange={handleDateChange}
          />
        </div>
        <div className='picker-wrapper'>
          <TimePickerComponent
            disabledTime={disabledTime}
            value={setTime()}
            onChange={handleTimeChange}
          />
        </div>
        {sendDate && sendTime && (
          <div className='summary-box'>
            <TextComponent
              text='Send Time'
              styleType='success'
              weight='extra-bold'
            />
            <TextComponent
              text={`${
                CREATE_CAMPAIGN_SCREEN.NEXT_SEND_TIME_AT
              } ${getDateString(sendDate)} at ${getTimeString(sendTime)}.`}
            />
          </div>
        )}
      </div>
    </TabContentWrapper>
  );
};
